import FormField from '@components/Common/FormField';
import React from 'react';

/**
 * A value edited as JSON: the fallback for anything the field map does not know, or a value whose
 * shape is not the one its field expects.
 *
 * The typed text is held locally and only propagated once it parses, so an intermediate keystroke
 * can never write malformed structure into the document the public renderer will read.
 */
const JsonField: React.FC<{
  name: string;
  value: unknown;
  onChange: (next: unknown) => void;
  /** Why this one is JSON, shown until the author makes an error worth showing instead. */
  note?: string;
  label?: string;
}> = ({ name, value, onChange, note, label = '' }) => {
  const serialised = React.useMemo(() => JSON.stringify(value, null, 2) ?? '', [value]);
  const [draft, setDraft] = React.useState(serialised);
  const [error, setError] = React.useState('');

  // Follow external changes (re-import, version load) unless the author is mid-edit with something
  // that does not parse.
  React.useEffect(() => {
    if (!error) setDraft(serialised);
  }, [serialised, error]);

  return (
    <FormField
      required={false}
      multiline
      name={name}
      label={label}
      value={draft}
      error={!!error}
      helperText={error || note || 'This section is nested, so it is edited as JSON.'}
      textFieldProps={{
        rows: undefined,
        minRows: 5,
        slotProps: {
          input: { sx: { fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' } },
        },
      }}
      onChange={(_n, v) => {
        const next = String(v);
        setDraft(next);
        try {
          onChange(JSON.parse(next));
          setError('');
        } catch (err) {
          setError((err as Error).message);
        }
      }}
    />
  );
};

export default JsonField;
